import React,{useMemo,useState} from 'react';
import {Link} from 'react-router-dom';
import {useMatches,useStandings} from '../hooks/useSupabase';
import {formatMatchDate,formatMatchTime,isAoxTeam,matchDateTime} from '../lib/matchday';
import {seasonForDate as getSeasonForDate,isLeagueMatch,isCupMatch,matchResult as resultForMatch,reconcileLeagueStandings} from '../lib/leagueSeason';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTrendingUp,FiCalendar,FiArrowRight,FiHome,FiMapPin}=FiIcons;

const categoryLabel=(category)=> category==='Senior' ? "Α' Ομάδα" : category;

const resultClass=(result)=> ({
  W: 'bg-emerald-500 text-white',
  D: 'bg-amber-400 text-amber-950',
  L: 'bg-rose-500 text-white',
}[result] || 'bg-slate-200 text-slate-600');

const SeasonDashboard=({compact=false,defaultCategory='Senior',defaultSeason='2026-27'})=> {
  const {data: matchesData,loading: matchesLoading}=useMatches();
  const {data: standingsData,loading: standingsLoading}=useStandings();
  const matches=matchesData || [];
  const standings=standingsData || [];
  const [category,setCategory]=useState(defaultCategory);
  const [season,setSeason]=useState(defaultSeason);

  const categories=useMemo(()=> {
    const values=new Set(['Senior']);
    standings.forEach((row)=> row.team_category && values.add(row.team_category));
    matches.forEach((row)=> row.team_category && values.add(row.team_category));
    return Array.from(values).sort((a,b)=> a==='Senior' ? -1 : b==='Senior' ? 1 : a.localeCompare(b,'el'));
  },[matches,standings]);

  const seasons=useMemo(()=> {
    const values=new Set();
    standings.filter((row)=> (row.team_category || 'Senior')===category).forEach((row)=> row.season_year && values.add(row.season_year));
    matches.filter((row)=> (row.team_category || 'Senior')===category).forEach((row)=> {
      const derived=getSeasonForDate(row.date);
      if (derived) values.add(derived);
    });
    return Array.from(values).sort().reverse();
  },[category,matches,standings]);

  const seasonMatches=useMemo(()=> matches
    .filter((match)=> (match.team_category || 'Senior')===category && getSeasonForDate(match.date)===season && (isAoxTeam(match.home_team) || isAoxTeam(match.away_team)) && !['temporary','friendly','academy'].includes(match.source_type))
    .sort((a,b)=> (matchDateTime(a)?.getTime() || 0) - (matchDateTime(b)?.getTime() || 0)),[matches,category,season]);

  const leagueMatches=seasonMatches.filter(isLeagueMatch);
  const completed=leagueMatches.filter((match)=> match.status==='completed' && resultForMatch(match));
  const results=completed.map((match)=> ({match,result: resultForMatch(match)}));
  const recent=results.slice(-5).reverse();
  const now=new Date();
  const isPending=(match)=> !['completed','canceled','cancelled','postponed'].includes(match.status) &&
    (['live','half_time','second_half'].includes(match.status) || (matchDateTime(match)?.getTime() || 0)>=now.getTime());
  const nextMatch=seasonMatches.find(isPending) || null;
  const nextLeagueMatch=leagueMatches.find(isPending) || null;
  const latestResults=completed.slice(-3).reverse();
  const leagueTable=useMemo(()=>reconcileLeagueStandings(standings,matches,category,season),[standings,matches,category,season]);
  const standingRow=leagueTable.find((row)=>isAoxTeam(row.team));
  const tableSize=leagueTable.length;
  const homeCompleted=completed.filter((match)=> isAoxTeam(match.home_team));
  const awayCompleted=completed.filter((match)=> isAoxTeam(match.away_team));
  const countRecord=(list)=> list.reduce((acc,match)=> {
    const result=resultForMatch(match);
    if (result==='W') acc.w +=1;
    if (result==='D') acc.d +=1;
    if (result==='L') acc.l +=1;
    return acc;
  },{w: 0,d: 0,l: 0});
  const homeRecord=countRecord(homeCompleted);
  const awayRecord=countRecord(awayCompleted);
  const streak=(()=> {
    const ordered=results.slice().reverse();
    if (!ordered.length) return '—';
    const first=ordered[0].result;
    let count=0;
    for (const item of ordered) {
      if (item.result !==first) break;
      count +=1;
    }
    return `${first}${count}`;
  })();
  const loading=matchesLoading || standingsLoading;

  if (loading) return <div className="rounded-2xl bg-white p-6 text-center text-gray-500 shadow-sm">Φόρτωση σεζόν...</div>;

  return (
    <section className={`rounded-[2rem] border border-slate-200 bg-white shadow-xl overflow-hidden ${compact ? '' : 'mb-10'}`}>
      <div className="bg-gradient-to-r from-primary-900 via-primary-700 to-blue-600 px-5 py-5 md:px-7 text-white">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-blue-200">Season Dashboard</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-black">{categoryLabel(category)} • {season}</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {!compact && <select value={category} onChange={(e)=> setCategory(e.target.value)} className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-bold text-white">{categories.map((item)=> <option className="text-gray-900" key={item} value={item}>{categoryLabel(item)}</option>)}</select>}
            {!compact && <select value={season} onChange={(e)=> setSeason(e.target.value)} className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-bold text-white">{seasons.map((item)=> <option className="text-gray-900" key={item} value={item}>{item}</option>)}</select>}
          </div>
        </div>
      </div>
      <div className="p-5 md:p-7">
        <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">Στατιστικά πρωταθλήματος {category==='Senior'&&season==='2026-27'?'• Γ΄ Εθνική, 8ος Όμιλος':''}</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
          <Stat label="Θέση" value={standingRow ? `${standingRow.position}${tableSize ? `/${tableSize}` : ''}` : '—'} />
          <Stat label="Αγ." value={standingRow?.played ?? completed.length} />
          <Stat label="Ν" value={standingRow?.won ?? results.filter((item)=> item.result==='W').length} />
          <Stat label="Ι" value={standingRow?.drawn ?? results.filter((item)=> item.result==='D').length} />
          <Stat label="Η" value={standingRow?.lost ?? results.filter((item)=> item.result==='L').length} />
          <Stat label="ΓΥ" value={standingRow?.gf ?? '—'} />
          <Stat label="ΓΚ" value={standingRow?.ga ?? '—'} />
          <Stat label="Βαθ." value={standingRow?.points ?? '—'} strong />
        </div>
        <div className="mt-6 grid gap-5 lg:grid-cols-[1.2fr_1fr]">
          <div className="rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between gap-4">
              <h3 className="font-black text-gray-900 flex items-center"><SafeIcon icon={FiTrendingUp} className="mr-2 h-5 w-5 text-primary-600" />Πρόσφατη φόρμα</h3>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">Streak {streak}</span>
            </div>
            <div className="mt-4 flex gap-2">{recent.length ? recent.map(({match,result})=> <Link to={`/matches/${match.id}`} key={match.id} className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-black shadow-sm ${resultClass(result)}`} title={`${match.home_team} ${match.home_score}-${match.away_score} ${match.away_team}`}>{result}</Link>) : <span className="text-sm text-gray-500">Δεν υπάρχουν ολοκληρωμένοι αγώνες ακόμη.</span>}</div>
            {!compact && <div className="mt-5 grid grid-cols-2 gap-3 text-sm"><Record label="Εντός" icon={FiHome} record={homeRecord} /><Record label="Εκτός" icon={FiMapPin} record={awayRecord} /></div>}
          </div>
          <div className="rounded-2xl border border-primary-100 bg-primary-50 p-5">
            <div className="text-xs font-black uppercase tracking-[0.16em] text-primary-700">Επόμενος αγώνας · Όλες οι διοργανώσεις</div>
            {nextMatch ? <><div className="mt-2 text-xs font-bold text-blue-800">{isCupMatch(nextMatch) ? '🏆 Κύπελλο Ελλάδας' : isLeagueMatch(nextMatch) ? '⚽ Πρωτάθλημα' : nextMatch.tournament || 'Άλλη διοργάνωση'}</div><div className="mt-2 text-lg font-black text-gray-900">{nextMatch.home_team} – {nextMatch.away_team}</div><div className="mt-2 text-sm text-gray-600 flex flex-wrap gap-x-3 gap-y-1"><span><SafeIcon icon={FiCalendar} className="mr-1 inline h-4 w-4" />{formatMatchDate(nextMatch)}</span>{nextMatch.time && <span>{formatMatchTime(nextMatch)}</span>}</div><Link to={`/matches/${nextMatch.id}`} className="mt-4 inline-flex items-center font-black text-primary-700">Match Center <SafeIcon icon={FiArrowRight} className="ml-1 h-4 w-4" /></Link></> : <p className="mt-2 text-sm text-gray-600">Δεν υπάρχει επόμενος αγώνας καταχωρημένος.</p>}
            {nextLeagueMatch && nextLeagueMatch.id !== nextMatch?.id && <div className="mt-4 border-t border-primary-100 pt-3 text-xs text-slate-700"><strong>Επόμενος στο πρωτάθλημα:</strong><div className="mt-1">{nextLeagueMatch.home_team} – {nextLeagueMatch.away_team}</div><div>{formatMatchDate(nextLeagueMatch)} {formatMatchTime(nextLeagueMatch)}</div></div>}
          </div>
        </div>
        {!compact && latestResults.length > 0 && <div className="mt-6"><h3 className="mb-3 font-black text-gray-900">Τελευταία αποτελέσματα</h3><div className="grid gap-3 md:grid-cols-3">{latestResults.map((match)=> <Link key={match.id} to={`/matches/${match.id}`} className="rounded-xl border border-slate-200 p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors"><div className="text-xs text-gray-500">{formatMatchDate(match)}</div><div className="mt-1 font-bold text-gray-900">{match.home_team}</div><div className="text-2xl font-black text-primary-700">{match.home_score} - {match.away_score}</div><div className="font-bold text-gray-900">{match.away_team}</div></Link>)}</div></div>}
      </div>
    </section>
  );
};

const Stat=({label,value,strong=false})=> <div className="rounded-xl bg-slate-50 p-4 text-center"><div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div><div className={`mt-1 text-2xl ${strong ? 'font-black text-primary-700' : 'font-black text-gray-900'}`}>{value}</div></div>;
const Record=({label,icon,record})=> <div className="rounded-xl bg-slate-50 p-3"><div className="flex items-center font-bold text-gray-700"><SafeIcon icon={icon} className="mr-2 h-4 w-4 text-primary-600" />{label}</div><div className="mt-1 font-black text-gray-900">{record.w}Ν • {record.d}Ι • {record.l}Η</div></div>;

export default SeasonDashboard;
